import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import ReportBranchPicker from "../components/ReportBranchPicker";

const ReportsPage = () => {
  const { branchId: routeBranchId } = useParams();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const storedScope = localStorage.getItem("reportBranchScope");
  const defaultBranchScope =
    storedScope || routeBranchId || localStorage.getItem("selectedBranchId") || "";
  const [branchScopeId, setBranchScopeId] = useState(defaultBranchScope);
  const resolvedBranchId = branchScopeId;
  const canSelectBranch = hasPermission(user, "branch.access.any");

  useEffect(() => {
    if (branchScopeId) {
      localStorage.setItem("reportBranchScope", branchScopeId);
    }
  }, [branchScopeId]);

  useEffect(() => {
    if (!canSelectBranch && branchScopeId === "all") {
      const fallback = localStorage.getItem("selectedBranchId") || "";
      setBranchScopeId(fallback);
    }
  }, [canSelectBranch, branchScopeId]);

  const canViewFinance = useMemo(
    () =>
      ["sales.read", "stock.read", "branch_expense.read", "client.read"].every(
        (permission) => hasPermission(user, permission)
      ),
    [user]
  );
  const canViewSales = useMemo(
    () =>
      ["sales.read", "branch_expense.read"].every((permission) =>
        hasPermission(user, permission)
      ),
    [user]
  );
  const canViewReceivables = hasPermission(user, "client.read");
  const canViewExpenses = hasPermission(user, "branch_expense.read");
  const canViewStock = hasPermission(user, "stock.read");
  const canViewCashLogs = hasPermission(user, "cash_register.logs.read");

  const reportCards = [
    {
      id: "sales",
      title: "Ventas",
      description: "Resumen de ingresos, egresos y margen por periodo.",
      enabled: canViewSales,
      path: resolvedBranchId
        ? `/branches/${resolvedBranchId}/reports/sales`
        : null,
    },
    {
      id: "finance",
      title: "Finanzas",
      description: "Ventas, stock y gastos por periodo.",
      enabled: canViewFinance,
      path: resolvedBranchId
        ? `/branches/${resolvedBranchId}/reports/finances`
        : null,
    },
    {
      id: "receivables",
      title: "Cobranza",
      description: "Estado de pagos y cuentas por cobrar.",
      enabled: canViewReceivables,
      path: resolvedBranchId
        ? `/branches/${resolvedBranchId}/reports/receivables`
        : null,
    },
    {
      id: "expenses",
      title: "Gastos",
      description: "Gastos por sucursal y categoria.",
      enabled: canViewExpenses,
      path: resolvedBranchId
        ? `/branches/${resolvedBranchId}/expenses`
        : null,
    },
    {
      id: "stock",
      title: "Stock",
      description: "Costos y movimientos de stock.",
      enabled: canViewStock,
      path: resolvedBranchId ? `/branches/${resolvedBranchId}/stock` : null,
    },
    {
      id: "cash-logs",
      title: "Historial de cajas",
      description: "Historial de aperturas, cierres y movimientos.",
      enabled: canViewCashLogs,
      path: resolvedBranchId
        ? `/branches/${resolvedBranchId}/cash-register-logs`
        : null,
    },
  ];

  const visibleCards = reportCards.filter((card) => card.enabled && card.path);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-2">Reportes</h1>
        <p className="text-gray-600 mb-6">
          Accesos rapidos a reportes y listados.
        </p>

        {visibleCards.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-4 text-gray-700">
            No tienes permisos para ver reportes en esta sucursal.
          </div>
        ) : (
          <div className="space-y-4">
            {canSelectBranch && (
              <ReportBranchPicker
                value={branchScopeId}
                onChange={setBranchScopeId}
                className="w-full"
              />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleCards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => navigate(card.path)}
                  className="text-left bg-white rounded-lg shadow p-4 hover:shadow-lg transition-shadow border border-transparent hover:border-blue-200"
                >
                  <h2 className="text-lg font-semibold mb-1">{card.title}</h2>
                  <p className="text-sm text-gray-600">{card.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
